import { ATTACHMENT_NODE_TYPE, ATTACHMENT_PAYLOAD_KEY, checkAttachmentRef } from "./attachments";
import { HASHTAG_NODE_TYPE, isHashtagText } from "./hashtags";
import {
  TOKEN_NODE_TYPE,
  TOKEN_PAYLOAD_KEY,
  tokenLabel,
  type InlineToken,
  type TokenRegistry,
} from "./tokens";
import { TABLE_CELL_NODE_TYPE, TABLE_NODE_TYPE, TABLE_ROW_NODE_TYPE } from "./tables";
import type { BlockType, EditorPreset, SerializedNodeLike } from "./types";

/** What a guard may consult besides the node itself. */
export interface GuardContext {
  tokens: TokenRegistry;
  /** The type of the node this one is a child of; `null` at the top of a document or fragment. */
  parent?: string | null;
  /**
   * Set for content on its way in (a paste, an import) rather than a stored document. Such content
   * may hold what the editor cannot, as long as the gate that checked it turns it into something
   * it can: see `normalizeTable`.
   */
  fragment?: boolean;
}

/** Returns a description of what is wrong with the node, or `null` when its fields are well formed. */
export type NodeGuard = (node: SerializedNodeLike, context: GuardContext) => string | null;

/**
 * Something the user can do with a capability, as data: what it is called and how it is found.
 * What it does to the document is mapped from `id` in `src/lexical/commands.ts`, so core stays
 * free of the editing engine. The slash menu, "Turn into", and `set-block` all read this list.
 */
interface CommandBase {
  id: string;
  label: string;
  /** Extra words the slash menu matches, besides the label. */
  keywords: readonly string[];
  group: CommandGroup;
  /** Left out of a menu until the query asks for it, to keep short lists short. */
  secondary?: boolean;
}

export type CapabilityCommand =
  /** Converts the current block (or the selected ones) to `block`. */
  (CommandBase & { action: "turn-into"; block: BlockType }) | (CommandBase & { action: "insert" });

export type CommandGroup = "text" | "list" | "insert";

export type CapabilityKind = "structure" | "inline-format" | "inline" | "block";

/**
 * One supported kind of content. The registry is the single list behind document validation,
 * preset filtering, and toolbar availability; later increments read the same entries for the slash
 * menu and "Turn into".
 */
export interface Capability {
  id: string;
  label: string;
  kind: CapabilityKind;
  /** Presets whose UI offers this capability. Loading content never depends on this list. */
  presets: readonly EditorPreset[];
  /** Serialized node types this capability owns, each with a guard run before deserialization. */
  nodes: Readonly<Record<string, NodeGuard>>;
  /** What the user can do with it. Offered only in `presets`. */
  commands?: readonly CapabilityCommand[];
}

const ALL: readonly EditorPreset[] = ["composer", "description", "document"];
const RICH: readonly EditorPreset[] = ["description", "document"];

const isString = (value: unknown): value is string => typeof value === "string";
const isNumber = (value: unknown): value is number =>
  typeof value === "number" && Number.isFinite(value);
const isOptional = <T>(value: unknown, is: (value: unknown) => value is T): boolean =>
  value === undefined || value === null || is(value);

/** A check that needs nothing but the node. Usable wherever a `NodeGuard` is expected. */
type Shape = (node: SerializedNodeLike) => string | null;

const element: Shape = (node) => (Array.isArray(node.children) ? null : "children is not a list");

const leaf: Shape = (node) => (node.children === undefined ? null : "leaf node has children");

const text: Shape = (node) => {
  if (!isString(node.text)) return "text is not a string";
  if (!isOptional(node.format, isNumber)) return "format is not a number";
  if (!isOptional(node.style, isString)) return "style is not a string";
  if (!isOptional(node.mode, isString)) return "mode is not a string";
  return leaf(node);
};

const heading: Shape = (node) =>
  isString(node.tag) && /^h[1-6]$/.test(node.tag) ? element(node) : "tag is not h1-h6";

const list: Shape = (node) => {
  if (node.listType !== "bullet" && node.listType !== "number" && node.listType !== "check") {
    return "listType is not bullet, number, or check";
  }
  if (!isOptional(node.start, isNumber)) return "start is not a number";
  return element(node);
};

const listItem: Shape = (node) => {
  if (!isOptional(node.checked, (value) => typeof value === "boolean")) {
    return "checked is not a boolean";
  }
  return element(node);
};

const token: NodeGuard = (node, context) => {
  const problem = context.tokens.check(node[TOKEN_PAYLOAD_KEY]);
  if (problem !== null) return problem;
  // The label is stored twice, as the node's text and in the payload. They must agree, or what
  // the user sees would not be what the token says it is.
  if (node.text !== tokenLabel(node[TOKEN_PAYLOAD_KEY] as InlineToken)) {
    return "text is not the token label";
  }
  if (node.mode !== "token") return "mode is not token";
  return text(node);
};

const hashtag: Shape = (node) =>
  isString(node.text) && !isHashtagText(node.text) ? "text is not a hashtag" : text(node);

const link: Shape = (node) => (isString(node.url) ? element(node) : "url is not a string");

const autolink: Shape = (node) =>
  isOptional(node.isUnlinked, (value) => typeof value === "boolean")
    ? link(node)
    : "isUnlinked is not a boolean";

const code: Shape = (node) =>
  isOptional(node.language, isString) ? element(node) : "language is not a string";

const childTypes = (node: SerializedNodeLike): unknown[] =>
  (node.children ?? []).map((child) => (child as { type?: unknown } | null)?.type);

/**
 * A stored table is exactly what the editor writes: rows of single cells, all the same length,
 * holding paragraphs. Lexical would quietly repair anything else on load (split a merged cell, pad
 * a short row, drop a row that is not in a table), so it is reported here instead. A pasted table
 * may be any of those things; `checkFragment` normalizes it and says what it changed.
 */
const table: NodeGuard = (node, context) => {
  const shape = element(node);
  if (shape !== null) return shape;
  const rows = node.children ?? [];
  if (rows.length === 0) return "table has no rows";
  if (childTypes(node).some((type) => type !== TABLE_ROW_NODE_TYPE)) return "child is not a row";
  if (context.fragment === true) return null;
  if (context.parent !== "root") return "table is not at the top level";
  const width = (rows[0]!.children ?? []).length;
  if (rows.some((row) => (row.children ?? []).length !== width)) return "rows differ in length";
  if (!isOptional(node.colWidths, (value) => Array.isArray(value) && value.every(isNumber))) {
    return "colWidths is not a list of numbers";
  }
  return null;
};

const tableRow: NodeGuard = (node, context) => {
  const shape = element(node);
  if (shape !== null) return shape;
  if (context.parent !== TABLE_NODE_TYPE) return "row is not in a table";
  if ((node.children ?? []).length === 0) return "row has no cells";
  if (childTypes(node).some((type) => type !== TABLE_CELL_NODE_TYPE)) return "child is not a cell";
  return isOptional(node.height, isNumber) ? null : "height is not a number";
};

const isSpan = (value: unknown): value is number =>
  typeof value === "number" && Number.isInteger(value) && value >= 1;

const tableCell: NodeGuard = (node, context) => {
  const shape = element(node);
  if (shape !== null) return shape;
  if (context.parent !== TABLE_ROW_NODE_TYPE) return "cell is not in a row";
  const header = node.headerState;
  if (header !== 0 && header !== 1 && header !== 2 && header !== 3) return "headerState is not 0-3";
  if (!isOptional(node.colSpan, isSpan)) return "colSpan is not a positive integer";
  if (!isOptional(node.rowSpan, isSpan)) return "rowSpan is not a positive integer";
  if (!isOptional(node.width, isNumber)) return "width is not a number";
  if (!isOptional(node.backgroundColor, isString)) return "backgroundColor is not a string";
  if (!isOptional(node.verticalAlign, isString)) return "verticalAlign is not a string";
  if (context.fragment === true) return null;
  if ((node.colSpan ?? 1) !== 1 || (node.rowSpan ?? 1) !== 1)
    return "merged cells are not supported";
  if (isString(node.backgroundColor)) return "cell background colors are not supported";
  if (childTypes(node).some((type) => type !== "paragraph"))
    return "cell holds more than paragraphs";
  return null;
};

/**
 * An attachment is a block of its own at the top level, as a table is. Where the editor finds one
 * anywhere else it moves it out, so a stored one that is nested was not written by the editor.
 */
const attachment: NodeGuard = (node, context) => {
  const problem = checkAttachmentRef(node[ATTACHMENT_PAYLOAD_KEY]);
  if (problem !== null) return problem;
  if (context.fragment !== true && context.parent !== "root") {
    return "attachment is not at the top level";
  }
  return leaf(node);
};

const turnInto = (
  block: BlockType,
  label: string,
  group: CommandGroup,
  keywords: readonly string[],
): CapabilityCommand => ({ id: block, label, keywords, group, action: "turn-into", block });

export const CORE_CAPABILITIES: readonly Capability[] = [
  {
    id: "structure",
    label: "Text",
    kind: "structure",
    presets: ALL,
    nodes: { root: element, paragraph: element, text, linebreak: leaf, tab: text },
    commands: [turnInto("paragraph", "Text", "text", ["paragraph", "plain", "body"])],
  },
  { id: "bold", label: "Bold", kind: "inline-format", presets: ALL, nodes: {} },
  { id: "italic", label: "Italic", kind: "inline-format", presets: ALL, nodes: {} },
  { id: "strikethrough", label: "Strikethrough", kind: "inline-format", presets: ALL, nodes: {} },
  { id: "inline-code", label: "Inline code", kind: "inline-format", presets: ALL, nodes: {} },
  { id: "link", label: "Link", kind: "inline", presets: ALL, nodes: { link, autolink } },
  {
    id: "heading",
    label: "Heading",
    kind: "block",
    presets: RICH,
    nodes: { heading },
    commands: [
      turnInto("h1", "Heading 1", "text", ["title", "h1", "big"]),
      turnInto("h2", "Heading 2", "text", ["subtitle", "h2", "section"]),
      turnInto("h3", "Heading 3", "text", ["h3", "subsection"]),
      { ...turnInto("h4", "Heading 4", "text", ["h4"]), secondary: true },
      { ...turnInto("h5", "Heading 5", "text", ["h5"]), secondary: true },
      { ...turnInto("h6", "Heading 6", "text", ["h6"]), secondary: true },
    ],
  },
  {
    id: "quote",
    label: "Quote",
    kind: "block",
    presets: RICH,
    nodes: { quote: element },
    commands: [turnInto("quote", "Quote", "text", ["blockquote", "citation"])],
  },
  {
    id: "list",
    label: "List",
    kind: "block",
    presets: ALL,
    nodes: { list, listitem: listItem },
    commands: [
      turnInto("bullet", "Bulleted list", "list", ["unordered", "ul", "bullets"]),
      turnInto("number", "Numbered list", "list", ["ordered", "ol", "steps"]),
      turnInto("check", "To-do list", "list", ["todo", "task", "checkbox", "checklist"]),
    ],
  },
  {
    id: "code-block",
    label: "Code block",
    kind: "block",
    presets: ALL,
    nodes: { code, "code-highlight": text },
    commands: [turnInto("code", "Code block", "text", ["snippet", "pre", "monospace"])],
  },
  {
    id: "token",
    label: "Reference",
    kind: "inline",
    presets: ALL,
    nodes: { [TOKEN_NODE_TYPE]: token },
  },
  {
    id: "hashtag",
    label: "Tag",
    kind: "inline",
    presets: ALL,
    nodes: { [HASHTAG_NODE_TYPE]: hashtag },
  },
  {
    id: "divider",
    label: "Divider",
    kind: "block",
    presets: RICH,
    nodes: { horizontalrule: leaf },
    commands: [
      {
        id: "divider",
        label: "Divider",
        keywords: ["rule", "hr", "line", "separator"],
        group: "insert",
        action: "insert",
      },
    ],
  },
  {
    id: "table",
    label: "Table",
    kind: "block",
    // Offered by the document preset; `<Editor tables>` turns it on or off for any preset. A table
    // that is already in a document loads and edits everywhere, like all content.
    presets: ["document"],
    nodes: {
      [TABLE_NODE_TYPE]: table,
      [TABLE_ROW_NODE_TYPE]: tableRow,
      [TABLE_CELL_NODE_TYPE]: tableCell,
    },
    commands: [
      {
        id: "table",
        label: "Table",
        keywords: ["grid", "rows", "columns", "cells", "spreadsheet"],
        group: "insert",
        action: "insert",
      },
    ],
  },
  {
    id: "attachment",
    label: "Attachment",
    kind: "block",
    // No preset offers it: an editor has attachments when its host gives it somewhere to keep
    // them (`<Editor attachments>`). One that is already in a document loads everywhere.
    presets: [],
    nodes: { [ATTACHMENT_NODE_TYPE]: attachment },
    commands: [
      {
        id: "image",
        label: "Image",
        keywords: ["picture", "photo", "img", "upload"],
        group: "insert",
        action: "insert",
      },
      {
        id: "file",
        label: "File",
        keywords: ["attachment", "attach", "upload", "document", "pdf"],
        group: "insert",
        action: "insert",
      },
    ],
  },
];

/**
 * The commands a menu shows for a query: every primary command when the query is empty, otherwise
 * whatever matches the label or a keyword, labels that start with the query first.
 */
export function matchCommands(
  commands: readonly CapabilityCommand[],
  query: string,
): CapabilityCommand[] {
  const needle = query.trim().toLowerCase();
  if (needle === "") return commands.filter((command) => command.secondary !== true);
  const rank = (command: CapabilityCommand): number => {
    const label = command.label.toLowerCase();
    // A secondary command has to be asked for by name (`h4`, `heading4`), not stumbled on.
    if (command.secondary === true) {
      const exact = command.keywords.includes(needle) || label.replaceAll(" ", "") === needle;
      return exact ? 0 : -1;
    }
    if (label.startsWith(needle)) return 0;
    if (label.split(" ").some((word) => word.startsWith(needle))) return 1;
    if (command.keywords.some((keyword) => keyword.startsWith(needle))) return 2;
    return label.includes(needle) ? 3 : -1;
  };
  return commands
    .map((command, index) => ({ command, index, rank: rank(command) }))
    .filter((entry) => entry.rank >= 0)
    .toSorted((a, b) => a.rank - b.rank || a.index - b.index)
    .map((entry) => entry.command);
}

/**
 * Which capabilities an editor offers, where that differs from its preset: `{ table: true }` puts
 * tables in a description editor, `{ table: false }` takes them out of a document editor. This
 * is about what can be made; what loads is never narrowed.
 */
export type CapabilityOverrides = Readonly<Record<string, boolean | undefined>>;

export interface CapabilityRegistry {
  readonly capabilities: readonly Capability[];
  guardFor(nodeType: string): NodeGuard | undefined;
  has(id: string): boolean;
  /** Whether a preset's UI should offer the capability. */
  offers(preset: EditorPreset, id: string): boolean;
  /** Every command a preset offers, in registry order. */
  commandsFor(preset: EditorPreset, overrides?: CapabilityOverrides): readonly CapabilityCommand[];
  /** A command by ID, if the preset offers it. */
  command(
    preset: EditorPreset,
    id: string,
    overrides?: CapabilityOverrides,
  ): CapabilityCommand | undefined;
}

export function createCapabilityRegistry(
  capabilities: readonly Capability[] = CORE_CAPABILITIES,
): CapabilityRegistry {
  const guards = new Map<string, NodeGuard>();
  const byId = new Map<string, Capability>();
  for (const capability of capabilities) {
    byId.set(capability.id, capability);
    for (const [type, guard] of Object.entries(capability.nodes)) guards.set(type, guard);
  }
  const commandsFor = (
    preset: EditorPreset,
    overrides: CapabilityOverrides = {},
  ): CapabilityCommand[] =>
    capabilities
      .filter((capability) => overrides[capability.id] ?? capability.presets.includes(preset))
      .flatMap((capability) => capability.commands ?? []);
  return {
    capabilities,
    guardFor: (nodeType) => guards.get(nodeType),
    has: (id) => byId.has(id),
    offers: (preset, id) => byId.get(id)?.presets.includes(preset) ?? false,
    commandsFor,
    command: (preset, id, overrides) =>
      commandsFor(preset, overrides).find((command) => command.id === id),
  };
}

export const CORE_REGISTRY: CapabilityRegistry = createCapabilityRegistry();
